import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import "./BirthdayCelebration.css";

interface BirthdayCelebrationProps {
  name: string;
  storageKey: string;
}

const G_BLUE = "#3b7ddd";
const G_RED = "#e8503a";
const G_YELLOW = "#f5b400";
const G_GREEN = "#1aa260";
const FLAG_COLORS = [G_BLUE, G_RED, G_YELLOW, G_GREEN];
const CONFETTI_COLORS = [G_BLUE, G_RED, G_YELLOW, G_GREEN, "#9c27b0"];
const TITLE = "HAPPY BIRTHDAY";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const bez = (t: number, a: number, b: number, c: number) =>
  (1 - t) * (1 - t) * a + 2 * (1 - t) * t * b + t * t * c;

interface GarlandProps {
  text: string;
  width: number;
  height: number;
  reserveRight?: number;
  maxFlag?: number;
  onFlagClick: () => void;
}

const Garland: React.FC<GarlandProps> = ({
  text,
  width,
  height,
  reserveRight = 0,
  maxFlag = 40,
  onFlagClick,
}) => {
  const w = width || 600;
  const xL = 0.06;
  const xR = Math.max(0.7, Math.min(0.94, 1 - 0.06 - reserveRight / w));
  const cx = (xL + xR) / 2;
  const yTop = 0.12;
  const yCtrl = 0.92;

  const letters = Array.from(text);
  const total = letters.length;
  const availW = w * (xR - xL);
  const flagW = Math.max(14, Math.min(maxFlag, (availW / total) * 0.82));
  const flagH = flagW * 1.2;
  const fontPx = Math.max(9, Math.round(flagW * 0.5));

  const ropeD = `M ${(xL * 640).toFixed(1)} ${(yTop * 96).toFixed(1)} Q ${(
    cx * 640
  ).toFixed(1)} ${(yCtrl * 96).toFixed(1)} ${(xR * 640).toFixed(1)} ${(
    yTop * 96
  ).toFixed(1)}`;

  return (
    <div className="cb-garland" style={{ height }}>
      <svg
        className="cb-rope"
        viewBox="0 0 640 96"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={ropeD}
          fill="none"
          stroke="#33404f"
          strokeWidth={2}
          strokeLinecap="round"
          opacity={0.55}
        />
      </svg>

      <div className="cb-flags">
        {letters.map((ch, i) => {
          if (ch === " ") return null;
          const t = total > 1 ? i / (total - 1) : 0.5;
          const x = bez(t, xL, cx, xR) * 100;
          const y = bez(t, yTop, yCtrl, yTop) * height;
          const color = FLAG_COLORS[i % FLAG_COLORS.length];
          const isLight = color === G_YELLOW;
          return (
            <span
              key={i}
              className="cb-flag-anchor"
              style={{ left: `${x}%`, top: `${y}px` }}
              onClick={(e) => {
                e.stopPropagation();
                onFlagClick();
              }}
            >
              <span
                className="cb-flag"
                style={{
                  width: flagW,
                  height: flagH,
                  color: isLight ? "#33404f" : "#fff",
                  fontSize: fontPx,
                  ["--cb-flag" as string]: color,
                  animationDelay: `${(i % 6) * 0.12}s`,
                }}
              >
                <span className="cb-flag-letter">{ch}</span>
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

interface BalloonsProps {
  text: string;
  width: number;
  onBalloonClick: () => void;
}

const PITCH = 1.08;
const KNOT_DROP = 5;

const Balloons: React.FC<BalloonsProps> = ({
  text,
  width,
  onBalloonClick,
}) => {
  const w = width || 600;
  const letters = Array.from(text.trim().replace(/\s+/g, " "));
  const total = letters.length;
  if (total === 0) return null;

  const available = w * 0.9;
  const balloonW = Math.max(
    16,
    Math.min(46, available / ((total - 1) * PITCH + 1)),
  );
  const balloonH = balloonW * 1.18;
  const fontPx = Math.max(10, Math.round(balloonW * 0.46));
  const pitch =
    total > 1
      ? Math.min(balloonW * PITCH, (available - balloonW) / (total - 1))
      : 0;
  const clusterW = (total - 1) * pitch;
  const xStart = w / 2 - clusterW / 2;
  const baseY = 6;
  const gatherDrop = Math.max(44, balloonH * 0.95);
  const tailLen = 30;

  const slots = letters.map((ch, i) => {
    const t = total > 1 ? i / (total - 1) : 0.5;
    const x = total > 1 ? xStart + t * clusterW : w / 2;
    const yTop = baseY - Math.sin(t * Math.PI) * 5;
    return { ch, i, x, yTop };
  });

  const gx = w / 2;
  const tops = slots.map((s) => s.yTop + balloonH);
  const gy = (tops.length ? Math.max(...tops) : balloonH) + gatherDrop;
  const H = gy + tailLen + 10;

  const strings = slots
    .filter((s) => s.ch !== " ")
    .map(
      (s) =>
        `M ${s.x.toFixed(1)} ${(s.yTop + balloonH + KNOT_DROP).toFixed(
          1,
        )} L ${gx.toFixed(1)} ${gy.toFixed(1)}`,
    )
    .join(" ");
  const tail = `M ${gx.toFixed(1)} ${gy.toFixed(1)} Q ${(gx + 6).toFixed(1)} ${(
    gy +
    tailLen / 2
  ).toFixed(1)} ${gx.toFixed(1)} ${(gy + tailLen).toFixed(1)}`;

  return (
    <div
      className="cb-balloons"
      style={{
        height: H,
        transformOrigin: `50% ${(((gy + tailLen) / H) * 100).toFixed(2)}%`,
      }}
    >
      <svg
        className="cb-strings"
        viewBox={`0 0 ${w} ${H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={strings}
          fill="none"
          stroke="rgba(51, 64, 79, 0.45)"
          strokeWidth={1.1}
        />
        <path
          d={tail}
          fill="none"
          stroke="rgba(51, 64, 79, 0.6)"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
        <circle cx={gx} cy={gy} r={3} fill="rgba(51, 64, 79, 0.6)" />
      </svg>

      {slots.map((s) => {
        if (s.ch === " ") return null;
        const color = FLAG_COLORS[s.i % FLAG_COLORS.length];
        const isLight = color === G_YELLOW;
        return (
          <span
            key={s.i}
            className="cb-balloon-anchor"
            style={{ left: s.x, top: s.yTop }}
            onClick={(e) => {
              e.stopPropagation();
              onBalloonClick();
            }}
          >
            <span
              className="cb-balloon"
              style={{
                width: balloonW,
                height: balloonH,
                fontSize: fontPx,
                color: isLight ? "#33404f" : "#fff",
                ["--cb-flag" as string]: color,
              }}
            >
              <span className="cb-balloon-letter">{s.ch}</span>
            </span>
          </span>
        );
      })}
    </div>
  );
};

const BirthdayCelebration: React.FC<BirthdayCelebrationProps> = ({
  name,
  storageKey,
}) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [width, setWidth] = useState(600);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (localStorage.getItem(storageKey)) {
      return;
    }
    setVisible(true);
    if (!prefersReducedMotion()) {
      fireConfetti();
    }
  }, [storageKey]);

  useLayoutEffect(() => {
    if (!visible) return;
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.getBoundingClientRect().width || 600);
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    } else {
      window.addEventListener("resize", measure);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, [visible]);

  const fireConfetti = () => {
    const end = Date.now() + 1800;
    const frame = () => {
      confetti({
        particleCount: 6,
        startVelocity: 0,
        ticks: 320,
        gravity: 0.7,
        spread: 130,
        angle: 270,
        origin: { x: Math.random(), y: -0.1 },
        zIndex: 20000,
        colors: CONFETTI_COLORS,
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const handleFlagClick = () => {
    if (!prefersReducedMotion()) fireConfetti();
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(storageKey, "1");
    setClosing(true);
    setTimeout(() => setVisible(false), 360);
  };

  if (!visible) return null;

  return (
    <div className={`cb-celebrate ${closing ? "cb-leave" : "cb-enter"}`}>
      <div className="cb-inner" ref={innerRef}>
        <button
          className="cb-close"
          aria-label="Dismiss"
          onClick={handleClose}
        >
          ✕
        </button>

        <Garland
          text={TITLE}
          width={width}
          height={62}
          reserveRight={50}
          maxFlag={24}
          onFlagClick={handleFlagClick}
        />

        <Balloons
          text={name.toUpperCase()}
          width={width}
          onBalloonClick={handleFlagClick}
        />

        <p className="cb-msg">
          Wishing you a wonderful day from everyone at the team!
        </p>
      </div>
    </div>
  );
};

export default BirthdayCelebration;
